/**
 * Notification feed API client — Story 92.2 Task 2.
 *
 * Cursor-paginated feed + per-row mark-read + bulk mark-all-read. All calls
 * go through `notificationFetch` (cross-tenant aggregation rule, ADR-020 §6 —
 * `X-Resource-Tenant-Id` is NEVER attached). The shared `axiosInstance` and
 * Orval `customFetch` auto-inject that header and therefore must NOT be used
 * for the notification domain.
 *
 * Contract:
 *   GET  /api/notification?limit=50&cursor=<opaque>&status=all|unread|read&projectId=<guid>
 *   POST /api/notification/{id}/read           — 204 No Content
 *   POST /api/notification/read-all?projectId  — 204 No Content
 *
 * Backend coordination: `projectId` filter on the GET feed and `read-all`
 * are story-blocking additions tracked against Epic 91 (Story 91.5).
 */

import { notificationFetch } from '../api/notificationFetch';
import type { NotificationFeedResponse } from '../types/models/Notification';

export interface FetchNotificationsOptions {
  cursor?: string;
  limit?: number;
  status?: 'all' | 'unread' | 'read';
  projectId?: string;
  tenantId?: string;
}

const DEFAULT_LIMIT = 50;
const DEFAULT_STATUS = 'all' as const;

function buildFeedQueryString(opts: FetchNotificationsOptions): string {
  const params = new URLSearchParams();
  params.set('limit', String(opts.limit ?? DEFAULT_LIMIT));
  params.set('status', opts.status ?? DEFAULT_STATUS);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.projectId) params.set('projectId', opts.projectId);
  if (opts.tenantId) params.set('tenantId', opts.tenantId);
  return params.toString();
}

export async function fetchNotifications(
  opts: FetchNotificationsOptions = {}
): Promise<NotificationFeedResponse> {
  const qs = buildFeedQueryString(opts);
  const res = await notificationFetch(`/api/notification?${qs}`);
  if (!res.ok) {
    throw new Error(`fetchNotifications failed: ${res.status} ${res.statusText}`);
  }
  // The BE inbox is authoritative: every delivered notification is a row in the
  // feed regardless of the recipient's per-channel preferences (the In-App /
  // Web preference gates live SSE delivery, not inbox membership — see
  // NotificationOrchestrator step 5). The feed therefore renders the server
  // response verbatim, keeping `items`, `nextCursor`, and `unreadCount`
  // mutually consistent (a client-side row filter would desync the cursor and
  // the "Load more" / count UI).
  return (await res.json()) as NotificationFeedResponse;
}

export async function markAsRead(notificationId: string): Promise<void> {
  const res = await notificationFetch(
    `/api/notification/${encodeURIComponent(notificationId)}/read`,
    {
      method: 'POST',
    }
  );
  if (!res.ok) {
    throw new Error(`markAsRead failed: ${res.status} ${res.statusText}`);
  }
}

export interface MarkAllAsReadOptions {
  projectId?: string;
}

export async function markAllAsRead(opts: MarkAllAsReadOptions = {}): Promise<void> {
  const path = opts.projectId
    ? `/api/notification/read-all?projectId=${encodeURIComponent(opts.projectId)}`
    : '/api/notification/read-all';
  const res = await notificationFetch(path, { method: 'POST' });
  if (!res.ok) {
    throw new Error(`markAllAsRead failed: ${res.status} ${res.statusText}`);
  }
}
